import { useEffect } from "react"
import { Redirect } from "react-router-dom"
import styled from "styled-components"

import { ErrorLogIcon } from "@minuthq/meatball-ui-icons"
import {
  Banner,
  CircularProgress,
  colors,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { ImageBackdrop } from "src/components/ImageBackdrop/ImageBackdrop"
import { useFetchBillingInfoWithCreate } from "src/data/billing/queries/billingQueries"
import { usePostSubscription } from "src/data/subscription/queries/subscriptionQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { isLimitedPlan } from "src/data/user/logic/userPlan"
import { LIMITED_PLAN_ID } from "src/data/user/user"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import backdropImage from "src/ui/images/photo-houses-top.jpg"
import { MCard } from "src/ui/MCard/MCard"

export function OnboardLimitedPlan() {
  const user = useGetUser()
  const fetchBillingInfo = useFetchBillingInfoWithCreate({
    user,
  })

  const {
    mutate: mutatePostSubscription,
    isSuccess: isPostSubscriptionSuccess,
    isIdle: isPostSubscriptionIdle,
    isError: isPostSubscriptionError,
  } = usePostSubscription()

  const postSubscriptionRequestShouldRun =
    fetchBillingInfo.isSuccess &&
    isPostSubscriptionIdle &&
    !isLimitedPlan(user.current_plan)

  useEffect(() => {
    // Effect that runs after billing info is created,
    // as this request is dependent on the billing info existing in BE
    if (postSubscriptionRequestShouldRun) {
      mutatePostSubscription({
        plan_id: LIMITED_PLAN_ID,
      })
    }
  }, [postSubscriptionRequestShouldRun, mutatePostSubscription])

  if (fetchBillingInfo.isError || isPostSubscriptionError) {
    return <OnboardingLimitedPlanError />
  }

  if (
    fetchBillingInfo.isSuccess &&
    isPostSubscriptionSuccess &&
    // Need to check this as well, so we're sure the user has been refetched before we redirect
    isLimitedPlan(user.current_plan)
  ) {
    return <Redirect to={Routes.Dashboard.location().pathname} />
  }

  return <OnboardingLimitedPlanLoader />
}

function OnboardingLimitedPlanError() {
  const { t, langKeys } = useTranslate()
  return (
    <ImageBackdrop
      landscapeImageUrl={backdropImage}
      portraitImageUrl={backdropImage}
      backgroundColor={colors.neutral}
    >
      <StyledCard>
        <ErrorLogIcon width={40} height={40} />
        <Text variant="heading2">
          {t(langKeys.task_management_setting_up_account)}
        </Text>
        <Banner type="error">
          <BannerContent>
            <Text color="emergency">
              {t(langKeys.failed_something_went_wrong)}
            </Text>
          </BannerContent>
        </Banner>
      </StyledCard>
    </ImageBackdrop>
  )
}

function OnboardingLimitedPlanLoader() {
  const { t, langKeys } = useTranslate()

  return (
    <ImageBackdrop
      landscapeImageUrl={backdropImage}
      portraitImageUrl={backdropImage}
      backgroundColor={colors.neutral}
    >
      <StyledCard>
        <CircularProgress />
        <Text variant="heading2">
          {t(langKeys.task_management_setting_up_account)}
        </Text>
      </StyledCard>
    </ImageBackdrop>
  )
}

const StyledCard = styled(MCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.XL};
  max-width: 60ch;
  padding: ${spacing.XL3};
`

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: ${spacing.M};
`
